import { useSelector } from 'react-redux';
import React, { Fragment, useEffect, useState } from 'react';

import { Grid } from '@mui/material';

import { snackAlertData } from 'utils/constants';
import MatchedFilters from './RefundMatchedFilters';
import { useCustomMediaQuery } from 'hooks/useMediaQuery';
import { handleErrorAlert } from 'utils/handleErrorAlert';
import { useCompanyService } from 'store/services/company.service';
import { getBankAccountsDropdown } from 'store/services/bank.Service';
import { useInventoryService } from 'store/services/inventory.service';
import { matchedTableHeaders } from '../../utils/matchedReviewHeaders';
import { DataTableWithAccordion, CircleLoading, SnackBarAlert, TablePagination } from 'shared';

const initialFilters = {
    bankAccount: null,
    type: 'not_refund',
    description: '',
    startDate: null,
    endDate: null,
    amount: '',
    referenceId: '',
    matchedBy: null,
};

const RefundMatched = ({ selectedRows = [], onSelectionChange }) => {
    const { getCompanyUsersDropdown } = useCompanyService();
    const companyData = useSelector((state) => state.company.data);
    const isMobileScreen = useCustomMediaQuery('(max-width: 600px)');
    const { getInventoryForMatched } = useInventoryService();

    const [page, setPage] = useState(1);
    const [data, setData] = useState(null);
    const [banks, setBanks] = useState(null);
    const [users, setUsers] = useState(null);
    const [tableLoading, setTableLoading] = useState(true);
    const [alertData, setAlertData] = useState(snackAlertData);
    const [selected, setSelected] = useState(selectedRows || []);
    const [filterValues, setFilterValues] = useState(initialFilters);

    const handleGetInventoryForMatched = async (page, filters = {}) => {
        try {
            setTableLoading(true);
            const response = await getInventoryForMatched({
                companyId: companyData.id,
                page,
                type: 'not_refund',
                ...filters,
            });
            setData(response);
        } catch (error) {
            handleErrorAlert(setAlertData);
        } finally {
            setTableLoading(false);
        }
    };

    const handleGetBankAccountsDropdown = async () => {
        try {
            const response = await getBankAccountsDropdown(companyData?.id);
            const bankValues = response.data.map((bank) => {
                return { label: bank.name, value: bank.id };
            });
            setBanks([{ value: 'all', label: 'All accounts' }, ...bankValues]);
        } catch (error) {
            handleErrorAlert(setAlertData);
        }
    };

    const handleGetCompanyUsersDropdown = async () => {
        try {
            const response = await getCompanyUsersDropdown(companyData?.id);
            const userValues = response.data.map((user) => {
                return { label: user.name, value: user.id };
            });
            setUsers([{ value: 'all', label: 'All users' }, ...userValues]);
        } catch (error) {
            handleErrorAlert(setAlertData);
        }
    };

    const handleRemoveUnmatchedItem = (unmatchedId) => {
        setData((prevData) => {
            const newResults = prevData.results.filter((item) => item.id !== unmatchedId);
            const newCount = prevData.count - 1;
            const shouldGoToPrevPage = newResults.length === 0 && page > 1;

            if (shouldGoToPrevPage) {
                handlePageChange(page - 1);
            }

            return {
                ...prevData,
                count: newCount,
                results: newResults,
            };
        });
    };

    const handlePageChange = async (page) => {
        setPage(page);
        setSelected([]);
        await handleGetInventoryForMatched(page, filterValues);
    };

    const handleFiltersSubmit = async (values) => {
        try {
            setPage(1);
            setSelected([]);
            setFilterValues(values);
            handleGetInventoryForMatched(1, values);
        } catch (error) {
            handleErrorAlert(setAlertData);
        }
    };

    const handleSelectAllRows = (event) => {
        if (event.target.checked) {
            const selectedRows = [];
            data?.results?.forEach((cellData) => {
                if (!selected.includes(cellData.id)) {
                    selectedRows.push(cellData.id);
                }
            });
            setSelected(selectedRows);
        } else {
            setSelected([]);
        }
    };

    const handleSelectRow = (event, id) => {
        event.stopPropagation();

        const isSelected = selected.includes(id);
        const newSelected = isSelected ? [] : [id];

        const selectedRow = !isSelected && data?.results?.find((item) => item.id === id);

        setSelected(newSelected);

        if (onSelectionChange) {
            onSelectionChange(selectedRow ? [selectedRow] : []);
        }
    };

    useEffect(() => {
        if (companyData) {
            handleGetInventoryForMatched(page);
            handleGetBankAccountsDropdown();
            handleGetCompanyUsersDropdown();
        }
    }, [companyData]);

    return (
        <Fragment>
            {companyData?.id && data && banks && users ? (
                <Grid container>
                    <Grid item xs={12}>
                        <MatchedFilters
                            banks={banks}
                            users={users}
                            selected={selected}
                            setSelected={setSelected}
                            handleFiltersSubmit={handleFiltersSubmit}
                        />
                    </Grid>
                    {data?.count > 0 && (
                        <Grid
                            item
                            xs={12}
                            display="flex"
                            marginBottom="5px"
                            justifyContent="flex-end"
                        ></Grid>
                    )}
                    <Grid item xs={12}>
                        <DataTableWithAccordion
                            data={data}
                            selected={selected}
                            isSelectable={true}
                            isLoading={tableLoading}
                            setSelected={setSelected}
                            showHeaderCheckBox={false}
                            handleSelectRow={handleSelectRow}
                            handleSelectAllRows={handleSelectAllRows}
                            headers={matchedTableHeaders(null, null, false)}
                        />
                    </Grid>
                    <Grid item xs={12} p={'10px 0'}>
                        <TablePagination
                            isMobileScreen={isMobileScreen}
                            page={page}
                            count={data?.count}
                            onChange={(e, pageNumber) => handlePageChange(pageNumber)}
                        />
                    </Grid>
                </Grid>
            ) : (
                <CircleLoading />
            )}

            <SnackBarAlert alertData={alertData} setAlertData={setAlertData} />
        </Fragment>
    );
};

export default RefundMatched;
